import { inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { TempCacheDto } from "../models/temp-cache.model";
import { SessionCacheApiService } from "./session-cache-api.service";
import { LoginDetailsStorageService } from "./login-details-storage.service";

/** App sessionStorage keys mapped to api/cache type=temp fields. */
export const TEMP_DETAILS_STORAGE_KEYS = new Set(["tempQuoteId", "tempQuoteNumber"]);

const CACHE_TYPE = "temp";

@Injectable({
  providedIn: "root",
})
export class TempDetailsStorageService {
  private readonly sessionCacheApi = inject(SessionCacheApiService);
  private readonly loginDetailsStorage = inject(LoginDetailsStorageService);
  private readonly values = new Map<string, string>();
  private static bridgeInstalled = false;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private persistEnabled = true;
  private loaded = false;

  purgeNativeTempKeysFromBrowser(): void {
    if (typeof window === "undefined") {
      return;
    }
    const nativeRemove = Storage.prototype.removeItem;
    TEMP_DETAILS_STORAGE_KEYS.forEach((key) => {
      try {
        nativeRemove.call(window.sessionStorage, key);
      } catch {
        /* ignore */
      }
    });
  }

  /** Chain after LoginDetailsStorageService.initSessionStorageBridge(). */
  initSessionStorageBridge(): void {
    if (TempDetailsStorageService.bridgeInstalled || typeof window === "undefined") {
      return;
    }

    const storageProto = Object.getPrototypeOf(window.sessionStorage) as Storage;
    const wrappedGetItem = storageProto.getItem;
    const wrappedSetItem = storageProto.setItem;
    const wrappedRemoveItem = storageProto.removeItem;
    const store = this;

    storageProto.getItem = function (key: string): string | null {
      if (this === window.sessionStorage && TEMP_DETAILS_STORAGE_KEYS.has(key)) {
        return store.getItem(key);
      }
      return wrappedGetItem.call(this, key);
    };

    storageProto.setItem = function (key: string, value: string): void {
      if (this === window.sessionStorage && TEMP_DETAILS_STORAGE_KEYS.has(key)) {
        store.setItem(key, value);
        return;
      }
      wrappedSetItem.call(this, key, value);
    };

    storageProto.removeItem = function (key: string): void {
      if (this === window.sessionStorage && TEMP_DETAILS_STORAGE_KEYS.has(key)) {
        store.removeItem(key);
        return;
      }
      wrappedRemoveItem.call(this, key);
    };

    TempDetailsStorageService.bridgeInstalled = true;
  }

  getItem(key: string): string | null {
    if (!TEMP_DETAILS_STORAGE_KEYS.has(key)) {
      return null;
    }
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    if (!TEMP_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    this.values.set(key, String(value));
    this.schedulePersist();
  }

  removeItem(key: string): void {
    if (!TEMP_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    this.values.delete(key);
    this.schedulePersist();
  }

  clear(): void {
    this.values.clear();
    this.loaded = false;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
  }

  disablePersist(): void {
    this.persistEnabled = false;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
  }

  enablePersist(): void {
    this.persistEnabled = true;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  getCacheSessionId(): string | null {
    return this.loginDetailsStorage.getCacheSessionId();
  }

  buildCachePayload(overrides: Partial<TempCacheDto> = {}): TempCacheDto {
    const payload: TempCacheDto = {
      tempId: this.toCacheInt(this.getItem("tempQuoteId")),
      tempNumber: this.getItem("tempQuoteNumber") ?? "",
      ...overrides,
    };
    return {
      tempId: this.toCacheInt(payload.tempId),
      tempNumber: payload.tempNumber ?? "",
    };
  }

  applyFromCacheDto(data: TempCacheDto): void {
    if (data.tempId !== undefined && data.tempId !== null) {
      this.values.set("tempQuoteId", String(data.tempId));
    }
    if (data.tempNumber !== undefined && data.tempNumber !== null && data.tempNumber !== "") {
      this.values.set("tempQuoteNumber", String(data.tempNumber));
    }
    this.loaded = true;
  }

  loadFromCache(sessionId?: string | number): Observable<boolean> {
    const token = this.loginDetailsStorage.getItem("token");
    const sid = sessionId ?? this.getCacheSessionId();

    if (!token || !sid) {
      return of(false);
    }

    return this.sessionCacheApi.get<TempCacheDto>(CACHE_TYPE, sid).pipe(
      tap((data) => {
        if (data) {
          this.applyFromCacheDto(data);
        }
      }),
      map((data) => !!data),
      catchError(() => of(false))
    );
  }

  saveToCache(
    overrides: Partial<TempCacheDto> = {},
    sessionIdOverride?: string | number
  ): Observable<TempCacheDto | null> {
    const sessionId = sessionIdOverride ?? this.getCacheSessionId();
    if (!sessionId) {
      return of(null);
    }

    const payload = this.buildCachePayload(overrides);
    this.applyFromCacheDto(payload);

    return this.sessionCacheApi.set(CACHE_TYPE, sessionId, payload).pipe(
      map(() => payload),
      catchError(() => of(payload))
    );
  }

  removeFromCache(sessionId?: string | number): Observable<unknown> {
    const sid = sessionId ?? this.getCacheSessionId();
    if (!sid) {
      return of(null);
    }
    return this.sessionCacheApi.remove(CACHE_TYPE, sid);
  }

  persistTempDetails(tempId: number | string, tempNumber: string): Observable<TempCacheDto | null> {
    this.setItem("tempQuoteId", String(tempId));
    this.setItem("tempQuoteNumber", String(tempNumber));
    return this.saveToCache({
      tempId: this.toCacheInt(tempId),
      tempNumber: String(tempNumber),
    });
  }

  toCacheInt(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === "") {
      return 0;
    }
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
  }

  private schedulePersist(): void {
    if (!this.persistEnabled) {
      return;
    }
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      const sessionId = this.getCacheSessionId();
      if (sessionId) {
        this.saveToCache().subscribe();
      }
    }, 300);
  }
}
